(function () {
  let moveTimeout;

  const homePositions = new WeakMap();
  const cartRetry = { value: null };
  const pageRetry = { value: null };

  const BUNDLER_TARGET_SELECTOR = '.bundler-target-element, #bundler-target-element';
  const BUNDLER_MARKUP_SELECTOR = '.bndlr-container, [class*="bndlr-"]';
  const BUNDLER_ROOT_SELECTOR = `${BUNDLER_TARGET_SELECTOR}, ${BUNDLER_MARKUP_SELECTOR}`;

  const isMobile =
    window.matchMedia('(max-width: 768px), (hover: none) and (pointer: coarse)').matches;

  function isCartNotificationOpen() {
    return document.getElementById('cart-notification')?.classList.contains('open');
  }

  function isBundlerTarget(node) {
    return (
      node?.classList?.contains('bundler-target-element') ||
      node?.id === 'bundler-target-element'
    );
  }

  function isBundlerNode(node) {
    return isBundlerTarget(node) || node?.classList?.contains('bndlr-container') || node?.matches?.('[class*="bndlr-"]');
  }

  function normalizeBundlerTargets() {
    document.querySelectorAll('#bundler-target-element, [id="bundler-target-element"]').forEach((node) => {
      node.classList.add('bundler-target-element');
    });
  }

  function isNestedBundlerTarget(target) {
    let parent = target.parentElement;

    while (parent) {
      if (parent !== target && isBundlerTarget(parent)) return true;
      parent = parent.parentElement;
    }

    return false;
  }

  function getBundlerUnits() {
    normalizeBundlerTargets();

    const units = [];
    const claimed = new Set();
    const rootTarget = document.getElementById('bundler-target-element');
    const targets = rootTarget ? [rootTarget] : [...document.querySelectorAll(BUNDLER_TARGET_SELECTOR)];

    targets.forEach((target) => {
      if (claimed.has(target) || isNestedBundlerTarget(target)) return;

      const nodes = [target];
      claimed.add(target);
      target.querySelectorAll(BUNDLER_TARGET_SELECTOR).forEach((nested) => claimed.add(nested));

      let sibling = target.nextElementSibling;
      while (sibling && isBundlerNode(sibling) && !isBundlerTarget(sibling)) {
        nodes.push(sibling);
        claimed.add(sibling);
        sibling = sibling.nextElementSibling;
      }

      units.push({ anchor: target, nodes });
    });

    document.querySelectorAll(BUNDLER_MARKUP_SELECTOR).forEach((node) => {
      if (claimed.has(node)) return;
      if (node.closest(BUNDLER_TARGET_SELECTOR)) return;

      units.push({ anchor: node, nodes: [node] });
      claimed.add(node);
    });

    return units;
  }

  function rememberHome(node) {
    if (!node.parentElement || homePositions.has(node)) return;

    homePositions.set(node, {
      parent: node.parentElement,
      nextSibling: node.nextElementSibling,
    });
  }

  function restoreToHome(node) {
    const home = homePositions.get(node);

    if (!home?.parent?.isConnected) return;

    if (home.nextSibling && home.nextSibling.parentElement === home.parent) {
      home.parent.insertBefore(node, home.nextSibling);
      return;
    }

    home.parent.appendChild(node);
  }

  function hasBundlerContent(unit) {
    if (unit.nodes.some((node) => node !== unit.anchor && isBundlerNode(node))) return true;
    if (unit.anchor.querySelector(BUNDLER_MARKUP_SELECTOR)) return true;
    return unit.anchor.children.length > 0;
  }

  function getPlacementAnchor(wrapper) {
    return (
      wrapper.querySelector('.cart-notification__links') ||
      wrapper.querySelector('product-recommendations')
    );
  }

  function placeBundlerUnit(wrapper, unit) {
    const anchor = getPlacementAnchor(wrapper);
    const insertBefore = anchor || null;

    unit.nodes.forEach((node) => {
      if (insertBefore) {
        wrapper.insertBefore(node, insertBefore);
      } else {
        wrapper.appendChild(node);
      }
    });
  }

  function isUnitInWrapper(wrapper, unit) {
    return unit.nodes.every((node) => wrapper.contains(node));
  }

  function isUnitCorrectlyPlaced(wrapper, unit) {
    if (!isUnitInWrapper(wrapper, unit)) return false;

    const anchor = getPlacementAnchor(wrapper);
    if (!anchor) return true;

    const firstNode = unit.nodes[0];
    let node = anchor.previousElementSibling;

    while (node) {
      if (node === firstNode) return true;
      if (isBundlerNode(node) || isBundlerTarget(node)) {
        node = node.previousElementSibling;
        continue;
      }
      return false;
    }

    return false;
  }

  function nudgeBundler(unit) {
    requestAnimationFrame(() => {
      unit.nodes.forEach((node) => {
        try {
          node.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        } catch (error) {
          /* ignore */
        }
      });

      window.dispatchEvent(new Event('resize'));
      document.dispatchEvent(new CustomEvent('bundler:cart-slot-ready'));
    });
  }

  function moveBundlerTargets() {
    const wrapper = document.querySelector('.cart-notification-wrapper');
    if (!wrapper) return;

    const units = getBundlerUnits();
    units.forEach((unit) => unit.nodes.forEach(rememberHome));

    if (!isCartNotificationOpen()) {
      wrapper.querySelectorAll(BUNDLER_ROOT_SELECTOR).forEach((node) => {
        restoreToHome(node);
      });
      return;
    }

    units.forEach((unit) => {
      const shouldMove = isBundlerTarget(unit.anchor) || hasBundlerContent(unit);

      if (!shouldMove) return;
      if (isUnitCorrectlyPlaced(wrapper, unit)) return;

      placeBundlerUnit(wrapper, unit);
      nudgeBundler(unit);
    });
  }

  function scheduleMoveBundlerTargets() {
    clearTimeout(moveTimeout);
    moveTimeout = setTimeout(moveBundlerTargets, 50);
  }

  function startIntervalRetry(intervalRef, callback, maxAttempts, intervalMs) {
    clearInterval(intervalRef.value);

    let attempts = 0;
    callback();

    intervalRef.value = setInterval(() => {
      callback();
      attempts += 1;

      if (attempts >= maxAttempts) {
        clearInterval(intervalRef.value);
      }
    }, intervalMs);
  }

  function startCartOpenRetry() {
    const maxAttempts = isMobile ? 180 : 60;

    startIntervalRetry(
      cartRetry,
      () => {
        moveBundlerTargets();
        if (!isCartNotificationOpen()) clearInterval(cartRetry.value);
      },
      maxAttempts,
      500
    );
  }

  function startPageRetry() {
    normalizeBundlerTargets();
    if (!document.querySelector(BUNDLER_ROOT_SELECTOR)) return;

    const maxAttempts = isMobile ? 240 : 120;
    startIntervalRetry(pageRetry, moveBundlerTargets, maxAttempts, 500);
  }

  function isBundlerMutationNode(node) {
    if (node.nodeType !== 1) return false;

    return isBundlerNode(node) || !!node.querySelector?.(BUNDLER_ROOT_SELECTOR);
  }

  function isBundlerMutationTarget(target) {
    if (target.nodeType !== 1) return false;

    return isBundlerNode(target) || !!target.closest?.(BUNDLER_ROOT_SELECTOR);
  }

  function initObserver() {
    const observer = new MutationObserver((mutations) => {
      let shouldRun = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (isBundlerMutationNode(node)) shouldRun = true;
          });
        }

        if (isBundlerMutationTarget(mutation.target)) {
          shouldRun = true;
        }
      });

      if (shouldRun) scheduleMoveBundlerTargets();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'id'],
    });
  }

  function initCartNotificationHook() {
    const cartNotification = document.getElementById('cart-notification');
    if (!cartNotification) return;

    const onCartOpened = () => {
      scheduleMoveBundlerTargets();
      startCartOpenRetry();

      cartNotification.addEventListener(
        'transitionend',
        () => scheduleMoveBundlerTargets(),
        { once: true }
      );

      if (isMobile) {
        setTimeout(scheduleMoveBundlerTargets, 300);
        setTimeout(scheduleMoveBundlerTargets, 800);
        setTimeout(scheduleMoveBundlerTargets, 1500);
      }
    };

    const openObserver = new MutationObserver(() => {
      if (cartNotification.classList.contains('open')) {
        onCartOpened();
      } else {
        clearInterval(cartRetry.value);
        scheduleMoveBundlerTargets();
      }
    });

    openObserver.observe(cartNotification, { attributes: true, attributeFilter: ['class'] });
  }

  function initConsentHooks() {
    ['cf_init', 'cf_consent', 'cf_consent_loaded', 'cf_services_consent', 'cf_services_consent_loaded'].forEach(
      (eventName) => {
        window.addEventListener(eventName, () => {
          startPageRetry();
          scheduleMoveBundlerTargets();
        });
      }
    );
  }

  function initPubSubHook() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      scheduleMoveBundlerTargets();
      startCartOpenRetry();
    });
  }

  function initMobilePriming() {
    if (!isMobile) return;

    const primeBundler = () => {
      document.querySelectorAll(BUNDLER_TARGET_SELECTOR).forEach((target) => {
        rememberHome(target);
        try {
          target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        } catch (error) {
          /* ignore */
        }
      });
      scheduleMoveBundlerTargets();
    };

    document.addEventListener('touchstart', primeBundler, { once: true, passive: true });
    window.addEventListener('orientationchange', () => {
      setTimeout(scheduleMoveBundlerTargets, 300);
    });
  }

  function init() {
    normalizeBundlerTargets();
    scheduleMoveBundlerTargets();
    startPageRetry();
    initObserver();
    initCartNotificationHook();
    initConsentHooks();
    initPubSubHook();
    initMobilePriming();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('load', () => {
    startPageRetry();
    scheduleMoveBundlerTargets();
  });

  window.addEventListener('pageshow', scheduleMoveBundlerTargets);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) scheduleMoveBundlerTargets();
  });

  document.addEventListener('shopify:section:load', () => {
    startPageRetry();
    scheduleMoveBundlerTargets();
  });

  document.addEventListener('cart-notification:opened', () => {
    scheduleMoveBundlerTargets();
    startCartOpenRetry();
  });

  document.addEventListener('body:visible', scheduleMoveBundlerTargets);
})();
